import { useMemo, useRef, useState } from "react";

import { ConfirmDeleteDialog } from "@/components/ui/confirm-delete-dialog";
import { dismissGuard } from "@/components/ui/dismiss-guard";
import { ErrorSnackbar } from "@/components/ui/error-snackbar";
import { MountedWhileOpen } from "@/components/ui/mounted-while-open";
import {
  SpecLineDialog,
  type SpecLineDialogGroup,
  type SpecLineValues,
} from "@/components/ui/spec-line-dialog";
import {
  useCreateSpecLine,
  useDeleteSpecLine,
  useDuplicateSpecLine,
  useUpdateSpecLine,
  type SpecLineCreateResult,
} from "@/hooks/spec-editor-lines";
import type { SpecEditorTarget } from "@/hooks/spec-editor-target";
import type { SpecLinesGroup, SpecLinesLine } from "@/hooks/specifications";
import { ERRORS } from "@/lib/spec-editor-errors";
import type { ActiveRowIds } from "@/lib/spec-lines-display";

// The Editor's line dialogs (#807): `Add Specification Line` from a group
// card's `Add specification line` (`Groups/AddLineToGroup.tsx`), and the three
// a line row's ⋮ opens (#810) — `Edit specification line`, `Duplicate
// Specification Line` and the `Delete Line "<name>"` confirm
// (`Lines/LineMoreActions/{EditLineDetails,DuplicateLine,DeleteDialog}.tsx`) —
// wired to #800's line verbs. Every failure reaches the screen as its `ERRORS`
// string (#548 D1) on the shared `ErrorSnackbar`.
//
// **Nothing here guesses** (structure rulings item 8; ADR-0014 decision 5 as
// amended): each dialog holds until the server answers, closes on success, and
// stays open, input kept, on a failure.
//
// Every write names its target when it is sent (`useSpecEditorTarget`), so the
// cursor it carries is the view's as of the click.

/** The Active selection a dialog hands its caller — the line and its selection
 *  (`selectionId` `null` only if the answer carried no selection for the line). */
export type SpecActiveRow = ActiveRowIds;

/** The new line and its `Selection 1`, as the create answered them. */
function createdRow(result: SpecLineCreateResult): SpecActiveRow {
  const line = result.lines?.find((candidate) => candidate.id === result.lineId);
  return { lineId: result.lineId, selectionId: line?.selections[0]?.id ?? null };
}

/**
 * `Add Specification Line` (`AddLineToGroup.tsx`), one per group card and kept
 * mounted — its emptied-name flag and its held row are the card's own (#807
 * E13).
 *
 * - The group is the card's, locked and the only option; the form is seeded
 *   with it and an empty name whenever the group object changes, open or not
 *   (`useEffect([group])` — every lines page rebuilds every group, so a page
 *   landing resets a typed name; carried, Assumption 8).
 * - The X, Escape and the backdrop do nothing while the line is created
 *   (`handleDialogClose`'s `isSubmitting` return); Cancel is held anyway.
 * - A success closes at once and holds the new line and its first selection;
 *   **after the exit** the form and the write reset — which takes a showing
 *   `ADD_LINE` with it — and the held row is reported through `onCreated`.
 * - The held row is reported once, then forgotten, so a later exit with
 *   nothing created reports nothing — an owner-ruled parity break (#875 D3 b):
 *   the donor's `activeLineSelection` was never cleared, and every later exit,
 *   a cancel included, made the last added line active again.
 */
export function AddSpecLineDialog({
  group,
  target,
  open,
  onOpenChange,
  onCreated,
}: {
  group: SpecLinesGroup;
  target: SpecEditorTarget;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The new line and its selection, to make the Active selection. */
  onCreated: (row: SpecActiveRow) => void;
}) {
  const create = useCreateSpecLine();
  const pendingActiveRow = useRef<SpecActiveRow | null>(null);
  // The exit's `form.reset()` is a fresh seed object over the same group.
  const [resets, setResets] = useState(0);
  const seed = useMemo<SpecLineValues>(
    () => ({ groupId: group.id, name: "", notes: "" }),
    // `resets` is the exit's reset; the group object is the donor's key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [group, resets],
  );
  const groups = useMemo<SpecLineDialogGroup[]>(
    () => [{ id: group.id, name: group.name }],
    [group],
  );

  return (
    <>
      <SpecLineDialog
        open={open}
        onOpenChange={dismissGuard(onOpenChange, create.isPending)}
        mode="add"
        groups={groups}
        saving={create.isPending}
        seed={seed}
        onSubmit={(values) =>
          create.mutate(
            { ...target(), groupId: group.id, name: values.name },
            {
              onSuccess: (result) => {
                pendingActiveRow.current = createdRow(result);
                onOpenChange(false);
              },
            },
          )
        }
        onExited={() => {
          setResets((count) => count + 1);
          create.reset();
          const created = pendingActiveRow.current;
          pendingActiveRow.current = null;
          if (created) onCreated(created);
        }}
      />
      <ErrorSnackbar open={create.isError} message={ERRORS.ADD_LINE} onClose={create.reset} />
    </>
  );
}

interface LineDialogProps {
  line: SpecLinesLine;
  target: SpecEditorTarget;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface LineFormDialogProps extends LineDialogProps {
  /** Every group of the version, in the version's order — the lines page's. */
  groups: SpecLinesGroup[];
}

/**
 * `Edit specification line` (`EditLineDetails.tsx`): the group select open
 * over every group of the version, `Notes` and `Clear Note`, `Save`. A group
 * change moves the line to the end of the new group (server-side, #800). The X,
 * Escape and the backdrop close it even mid-save, as the donor passes `onClose`
 * straight through.
 */
export function EditSpecLineDialog({ open, ...props }: LineFormDialogProps) {
  return (
    <MountedWhileOpen open={open}>
      {(onExited) => <EditLineForm open={open} onExited={onExited} {...props} />}
    </MountedWhileOpen>
  );
}

function EditLineForm({
  line,
  groups,
  target,
  open,
  onOpenChange,
  onExited,
}: LineFormDialogProps & { onExited: () => void }) {
  const update = useUpdateSpecLine();
  // `useEffect([line])` (`EditLineDetails.tsx:47-53`): a new line object is a
  // new seed.
  const seed = useMemo<SpecLineValues>(
    () => ({ groupId: line.groupId, name: line.name || "", notes: line.notes || "" }),
    [line],
  );

  return (
    <>
      <SpecLineDialog
        open={open}
        onOpenChange={onOpenChange}
        mode="edit"
        groups={groups}
        saving={update.isPending}
        seed={seed}
        onSubmit={(values) =>
          update.mutate(
            {
              ...target(),
              lineId: line.id,
              name: values.name,
              notes: values.notes,
              groupId: values.groupId ?? line.groupId,
            },
            { onSuccess: () => onOpenChange(false) },
          )
        }
        onExited={onExited}
      />
      <ErrorSnackbar
        open={update.isError}
        message={ERRORS.UPDATE_LINE_DETAILS}
        onClose={update.reset}
      />
    </>
  );
}

/**
 * `Duplicate Specification Line` (`DuplicateLine.tsx`): every group listed,
 * locked on the line's own; the name prefilled as stored, no "(copy)"; the
 * submit reads `Add specification line`. The copy lands at the end of the
 * group and is not made active — the menu keeps the source row (#810).
 */
export function DuplicateSpecLineDialog({ open, ...props }: LineFormDialogProps) {
  return (
    <MountedWhileOpen open={open}>
      {(onExited) => <DuplicateLineForm open={open} onExited={onExited} {...props} />}
    </MountedWhileOpen>
  );
}

function DuplicateLineForm({
  line,
  groups,
  target,
  open,
  onOpenChange,
  onExited,
}: LineFormDialogProps & { onExited: () => void }) {
  const duplicate = useDuplicateSpecLine();
  const seed = useMemo<SpecLineValues>(
    () => ({ groupId: line.groupId, name: line.name || "", notes: "" }),
    [line],
  );

  return (
    <>
      <SpecLineDialog
        open={open}
        onOpenChange={onOpenChange}
        mode="duplicate"
        groups={groups}
        saving={duplicate.isPending}
        seed={seed}
        onSubmit={(values) =>
          duplicate.mutate(
            { ...target(), lineId: line.id, name: values.name },
            { onSuccess: () => onOpenChange(false) },
          )
        }
        onExited={onExited}
      />
      <ErrorSnackbar
        open={duplicate.isError}
        message={ERRORS.DUPLICATE_LINE}
        onClose={duplicate.reset}
      />
    </>
  );
}

/** The line confirm's extra clause (`DeleteDialog.tsx:73-75`). */
const LINE_DELETE_DESCRIPTION =
  "All Selections and details associated with this Line will also be permanently deleted.";

/**
 * `Delete Line "<name>"` (`DeleteDialog.tsx`): the shared confirm, the name as
 * stored, the cascade clause after the `?`. Delete spins and Cancel is held
 * while it runs; the X, Escape and the backdrop are not. A success clears the
 * Active selection, whichever row was active — even once the confirm was
 * dismissed mid-delete — and then closes (Assumption 10).
 * Content-sized and opening on its paper, as #805 measured this donor
 * component (Assumption 11).
 */
export function DeleteSpecLineDialog({
  open,
  ...props
}: LineDialogProps & { onDeleted: () => void }) {
  return (
    <MountedWhileOpen open={open}>
      {(onExited) => <DeleteLineConfirm open={open} onExited={onExited} {...props} />}
    </MountedWhileOpen>
  );
}

function DeleteLineConfirm({
  line,
  target,
  open,
  onOpenChange,
  onDeleted,
  onExited,
}: LineDialogProps & { onDeleted: () => void; onExited: () => void }) {
  const remove = useDeleteSpecLine();

  return (
    <>
      <ConfirmDeleteDialog
        open={open}
        onOpenChange={onOpenChange}
        label={`Line "${line.name || ""}"`}
        description={LINE_DELETE_DESCRIPTION}
        fullWidth={false}
        autoFocusConfirm={false}
        isDeleting={remove.isPending}
        onConfirm={() => {
          // The write's own promise, not `mutate`'s per-call `onSuccess`: the
          // X, Escape and the backdrop stay live mid-delete and the confirm
          // unmounts after its exit, which silences per-call callbacks. The
          // donor clears through its store, which outlives the dialog.
          remove.mutateAsync({ ...target(), lineId: line.id }).then(
            () => {
              onDeleted();
              onOpenChange(false);
            },
            // The failure is on the snackbar, through the hook's state.
            () => {},
          );
        }}
        onExited={onExited}
      />
      <ErrorSnackbar open={remove.isError} message={ERRORS.DELETE_LINE} onClose={remove.reset} />
    </>
  );
}
